import type { SigningProgressEvent, SigningRequest } from "@vericreator/contracts";
import { mostAdvancedSigningProgressEvent } from "@vericreator/contracts";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";

export interface SigningProgressTimeline {
  /** Local presentation epoch; it increments only when the broker announces a new runner attempt. */
  readonly attempt: number;
  /** Highest broker sequence consumed in the current attempt. */
  readonly lastSequence: number;
  /** Most advanced visible lifecycle event, not simply the last packet received. */
  readonly latest?: SigningProgressEvent;
  readonly byStage: Readonly<Record<string, SigningProgressEvent>>;
}

const reconnectDelaysMilliseconds = [1_000, 2_000, 4_000, 8_000, 15_000] as const;

/**
 * Opens a job-scoped server push subscription for active requests only.
 *
 * D1 remains the initial/recovery snapshot; no query loop is used while a
 * socket is healthy. If the network changes, a fresh one-time socket ticket
 * is requested through the regular JWT-authenticated API before reconnecting.
 */
export function useSigningProgress(
  accessToken: string,
  requests: readonly SigningRequest[],
  onTerminal: () => void
): Readonly<Record<string, SigningProgressTimeline>> {
  const [timelines, setTimelines] = useState<Readonly<Record<string, SigningProgressTimeline>>>({});
  const activeRequestIds = useMemo(
    () => requests
      .filter((request) => request.status === "queued" || request.status === "signing")
      .map((request) => request.id)
      .sort()
      .join(","),
    [requests]
  );

  useEffect(() => {
    if (!accessToken || !activeRequestIds) {
      return;
    }
    let cancelled = false;
    const sockets = new Map<string, WebSocket>();
    const retryTimers = new Map<string, number>();
    const terminalRequests = new Set<string>();

    const connect = (requestId: string, attempt: number) => {
      if (cancelled) return;
      void api.createSigningProgressSubscription(accessToken, requestId)
        .then(() => {
          if (cancelled) return;
          const socket = api.openSigningProgressSocket(requestId);
          sockets.set(requestId, socket);
          socket.onmessage = (message) => {
            try {
              const event = api.parseSigningProgressEvent(JSON.parse(String(message.data)));
              setTimelines((current) => {
                const timeline = current[requestId];
                if (timeline && event.sequence <= timeline.lastSequence) {
                  return current;
                }
                const byStage = {
                  ...(timeline?.byStage ?? {}),
                  [event.stage]: mergeStageObservation(timeline?.byStage[event.stage], event)
                };
                const latest = mostAdvancedSigningProgressEvent(byStage);
                return {
                  ...current,
                  [requestId]: {
                    attempt: timeline?.attempt ?? 1,
                    lastSequence: event.sequence,
                    byStage,
                    ...(latest ? { latest } : {})
                  }
                };
              });
              if (event.stage === "completed" || event.stage === "failed") {
                terminalRequests.add(requestId);
                onTerminal();
              }
            } catch {
              // A malformed broker payload must not become a fake signing
              // state. The signed request will still reconcile from D1.
            }
          };
          socket.onclose = (closeEvent) => {
            sockets.delete(requestId);
            // The server deliberately closes existing sockets when the owner
            // starts a new attempt. Sequence numbers restart from one in the
            // cleared Durable Object, so start a fresh visual epoch rather
            // than making a source download appear to move backwards inside
            // the same attempt. A normal source-stage update must never reset
            // this timeline.
            if (closeEvent.code === 1000 && closeEvent.reason === "signing retry started") {
              setTimelines((current) => {
                const previous = current[requestId];
                return {
                  ...current,
                  [requestId]: {
                    attempt: (previous?.attempt ?? 1) + 1,
                    lastSequence: 0,
                    byStage: {}
                  }
                };
              });
            }
            if (cancelled || terminalRequests.has(requestId)) return;
            const delay = reconnectDelaysMilliseconds[Math.min(attempt, reconnectDelaysMilliseconds.length - 1)]!;
            const timer = window.setTimeout(() => connect(requestId, attempt + 1), delay);
            retryTimers.set(requestId, timer);
          };
          socket.onerror = () => socket.close();
        })
        .catch(() => {
          if (cancelled || terminalRequests.has(requestId)) return;
          const delay = reconnectDelaysMilliseconds[Math.min(attempt, reconnectDelaysMilliseconds.length - 1)]!;
          const timer = window.setTimeout(() => connect(requestId, attempt + 1), delay);
          retryTimers.set(requestId, timer);
        });
    };

    for (const requestId of activeRequestIds.split(",")) {
      connect(requestId, 0);
    }
    return () => {
      cancelled = true;
      for (const timer of retryTimers.values()) window.clearTimeout(timer);
      for (const socket of sockets.values()) socket.close();
    };
  }, [accessToken, activeRequestIds, onTerminal]);

  return timelines;
}

/**
 * Browsers can reconnect while an observation is in flight. Preserve the
 * greatest known counter and never repaint a completed stage as running.
 * Platform enforces the same invariant server-side; this is a deliberately
 * defensive presentation reducer for a previously open socket.
 */
function mergeStageObservation(
  previous: SigningProgressEvent | undefined,
  candidate: SigningProgressEvent
): SigningProgressEvent {
  if (!previous || previous.state === "completed") {
    return previous ?? candidate;
  }
  if (previous.unit !== candidate.unit || previous.current === undefined || candidate.current === undefined) {
    return candidate;
  }
  const current = Math.max(previous.current, candidate.current);
  const total = Math.max(previous.total ?? 0, candidate.total ?? 0, current);
  return { ...candidate, current, ...(total > 0 ? { total } : {}) };
}
